import { useColorModeValue } from "@chakra-ui/react";
import type { Collection } from "types/collection";
import type { BuySellHoldData } from "../BuySellHoldChart/BuySellHoldChart";
import type { StockTickerData } from "api/stockTickerData";

export const parseBuySellRating = (buySellScore: string | undefined): number => {
  const scoreString = buySellScore ?? "";
  const regexBuySellRating = scoreString.match(/\d+(\.\d+)?/g);
  const parsedRating = regexBuySellRating?.[0] ? parseFloat(regexBuySellRating[0]) : NaN;
  const numericRating = !isNaN(parsedRating) ? Math.min(5, Math.max(0, parsedRating)) : 0;
  return numericRating;
};

export const parseBuySellRatingNumber = (buySellScore: number | undefined): number => {
  const numericRating = buySellScore ? Math.min(5, Math.max(0, buySellScore)) : 0;
  return numericRating;
};

export const generateBuySellData = (
  groupedCollections: Collection[],
  getStockData: (ticker: string, exchange: string) => StockTickerData | undefined
): BuySellHoldData => {
  const items = (groupedCollections || []).flatMap((collection) => {
    const { ticker, stockExchange, buySellScoreSentiment, id } = collection || {};
    if (!ticker || !stockExchange) return [];

    const stockData = getStockData?.(ticker, stockExchange);
    const price = parseFloat(stockData?.ticker_quote?.price ?? "0");
    const buySellScore = buySellScoreSentiment?.value;
    const rating = parseBuySellRating(buySellScore);

    if (!rating) return [];
    else return [{ projectId: id, ticker, currentPrice: price, rating }];
  });

  return { items };
};

// Define the types needed
export type RatingLabel = "Sell" | "Under Perform" | "Hold" | "Out Perform" | "Buy" | "--";

export interface BuySellChartColors {
  sell: string;
  underPerform: string;
  hold: string;
  outPerform: string;
  buy: string;
  noRating: string;
}

// Define the structure of a single rating configuration item
export interface RatingConfigItem {
  max: number;
  label: RatingLabel;
  color: string;
}

/**
 * Creates the rating configuration array based on provided chart colors.
 * @param chartColors - An object containing color strings for each rating level.
 * @returns The rating configuration array.
 */
export const createRatingConfig = (chartColors: BuySellChartColors): ReadonlyArray<RatingConfigItem> => {
  return [
    { max: 0, label: "--", color: chartColors.noRating },
    { max: 1, label: "Sell", color: chartColors.sell },
    { max: 2, label: "Under Perform", color: chartColors.underPerform },
    { max: 3, label: "Hold", color: chartColors.hold },
    { max: 4, label: "Out Perform", color: chartColors.outPerform },
    { max: Infinity, label: "Buy", color: chartColors.buy },
  ] as const; // Use 'as const' for better type inference and immutability
};

/**
 * Finds the rating configuration information (label, color) for a given numerical rating.
 * @param rating - The numerical rating (e.g., 0-5).
 * @param ratingConfig - The rating configuration array generated by createRatingConfig.
 * @returns The matching rating configuration item.
 */
export const getRatingInfo = (rating: number, ratingConfig: ReadonlyArray<RatingConfigItem>): RatingConfigItem => {
  if (rating === null || rating === undefined) {
    return ratingConfig[0];
  }
  return ratingConfig.find((config) => rating <= config.max) || ratingConfig[ratingConfig.length - 1];
};

export const useBuySellChartColors = () => ({
  buy: useColorModeValue("#A0D7FF", "#4A8CC7"),
  underPerform: useColorModeValue("#FFA0A0", "#CC4444"),
  hold: useColorModeValue("#FFE4A0", "#D4A012"),
  outPerform: useColorModeValue("#A0E4E2", "#4A9B99"),
  sell: useColorModeValue("#FFB5C0", "#CC5C66"),
  noRating: useColorModeValue("#D1D5DB", "#6B7280"),
});

export type ChartColors = ReturnType<typeof useBuySellChartColors>;

export const getColorForRating = (rating: number, chartColors: ChartColors): string => {
  if (rating === null || rating === undefined) {
    return chartColors.noRating;
  }
  if (rating <= 1) return chartColors.sell;
  else if (rating <= 2) return chartColors.underPerform;
  else if (rating <= 3) return chartColors.hold;
  else if (rating <= 4) return chartColors.outPerform;
  else return chartColors.buy;
};

export type ChartColorKeys = keyof ReturnType<typeof useBuySellChartColors>;
